<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import type { ComboboxInputEmits, ComboboxInputProps } from 'reka-ui'
import { reactiveOmit } from '@vueuse/core'
import { ComboboxInput, useForwardPropsEmits } from 'reka-ui'
import { cn } from '@ds/lib/utils'

/*
 * Bare inside the anchor, which is the control. The registry styles this as a
 * standalone bordered input — two frames deep once it sits in an anchor.
 */
const props = defineProps<ComboboxInputProps & {
  class?: HTMLAttributes['class']
}>()

const emits = defineEmits<ComboboxInputEmits>()

const delegatedProps = reactiveOmit(props, 'class')

const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
  <ComboboxInput
    v-bind="forwarded"
    :class="cn(
      // See DSInput on why `ds-control` is load-bearing (@tailwindcss/forms).
      'ds-control',
      'flex-1 min-w-0 bg-transparent border-0 p-0',
      'font-ds font-medium text-base text-foreground placeholder:text-muted-foreground',
      // `ds-control` earns the @tailwindcss/forms opt-out, but it also carries
      // the DS focus ring from ds-base.css — and here the anchor draws that.
      // Without this the field gets two rings, one inside the other.
      'focus:outline-none focus-visible:outline-none focus-visible:shadow-none',
      'disabled:cursor-not-allowed',
      props.class,
    )"
  >
    <slot />
  </ComboboxInput>
</template>
